import ComponentDomTree from "./DomTree";
import { CDashboardConfig, CDashboardModulePageAction, CDashboardModuleTabComponent, Context, PageAction, PageActions, PageActionsPayload, PageContext, PageParams } from "../../DashboardApp/types";
import { useEffect, useState, useContext, useCallback, useMemo } from "react";
import { Button, IconButton, Icon, Tooltip, Paper, Box, Divider, Tab, Typography, ListItemIcon, ListItemText, TextField } from "@mui/material";
import ComponentDomPreview from "./DomPreview";
import ComponentProperties from "./Properties";
import Stack from "@mui/material/Stack";
import { v4 as uuidv4 } from "uuid";
import { deleteComponent, findParentComponent, processDomTree } from "../dom-utils";
import DashboardPageContext from "../DashboardPageContext";
import ExecutionTypeContext, { Mode } from "../ExecutionTypeContext";
import { TabContext, TabList, TabPanel } from "@mui/lab";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import { componentsMap } from "../ComponentsMap";
import DashboardEnvContext from "../DashboardEnvContext";
import { Editor } from "@monaco-editor/react";
import { Mosaic, MosaicNode } from "react-mosaic-component";
import CircularProgress from "@mui/material/CircularProgress";
import styled from "@emotion/styled";
import "react-mosaic-component/react-mosaic-component.css";
import ReactJson from "react-json-view";
import PageActionsBoundary from "../PageActionsBoundary";
import { objectToString } from "../js-utils";

interface ErrorState {
	hasError: boolean;
	error?: string | undefined;
}

interface ComponentProps {
	component: CDashboardModuleTabComponent;
}

const designerLayout: MosaicNode<string> = {
	direction: "row",
	first: {
		direction: "column",
		first: "domTreePanel",
		second: "domPropsPanel",
		splitPercentage: 50,
	},
	second: {
		direction: "column",
		first: "previewPanel",
		second: "consolePanel",
		splitPercentage: 80,
	},
	splitPercentage: 30,
};

const CircularProgressFullScreenWrapper = styled.div`
	position: absolute;
	top: 0px;
	left: 0px;
	right: 0px;
	bottom: 0px;
	background: rgba(255, 255, 255, 0.4);
	z-index: 1;
`;

const CircularProgressWrapper = styled.div`
	position: absolute;
	top: 50%;
	left: 50%;
	width: 100px;
	text-align: center;
	margin-left: -50px;
`;

function Component({ component }: ComponentProps) {
	const { context, updateContext } = useContext(DashboardPageContext);
	const exeMode = useContext(ExecutionTypeContext);
	const dashboard = useContext(DashboardEnvContext);

	const [dashboardConfig, setDashboardConfig] = useState<CDashboardConfig>({} as CDashboardConfig);
	const [designerPageContext, setDesignerPageContext] = useState<Context>();
	const [designerPageParams, setDesignerPageParams] = useState<PageParams>();
	const [pageContent, setPageContent] = useState<CDashboardModuleTabComponent>();
	const [selectedComponent, setSelectedComponent] = useState<CDashboardModuleTabComponent>();
	const [locateComponent, setLocateComponent] = useState<CDashboardModuleTabComponent | undefined>();
	const [toPast, setToPast] = useState<CDashboardModuleTabComponent>();
	const [hasError, setHasError] = useState<ErrorState>({ hasError: false });
	const [refreshDesign, setRefreshDesign] = useState<number>(0);
	const [modVersion, setModVersion] = useState<number>(0);
	const [leftTopTabsValue, setLeftTopTabsValue] = useState<string>("0");
	const [leftBottomTabsValue, setLeftBottomTabsValue] = useState<string>("0");
	const [centerTabsValue, setCenterTabsValue] = useState<string>("0");
	const [consoleTabsValue, setConsoleTabsValue] = useState<string>("0");
	const [locatorActivated, setLocatorActivated] = useState<boolean>(false);
	const [anchorDomElementMenu, setAnchorDomElementMenu] = useState<null | HTMLElement>(null);
	const insertDomElementMenuOpen = Boolean(anchorDomElementMenu);
	const [pageLoaderActive, setPageLoaderActive] = useState<boolean>(false);
	const [pageDefinedActions, setPageDefinedActions] = useState<CDashboardModulePageAction[]>();

	useEffect(() => {
		const originalConsoleWarn = console.error;
		console.error = (...args) => {
			setHasError({ hasError: true, error: args.join(" ") });
		};
		return () => {
			console.error = originalConsoleWarn;
		};
	}, []);

	const logConsoleError = (message: string) => {
		setHasError({ hasError: true, error: message });
	};

	const updateDesignerContext = (newValue: Context) => {
		const newContext = JSON.parse(JSON.stringify(newValue)) as Context;
		setDesignerPageContext(newContext);
		return newValue;
	};

	const prepare = useCallback(async () => {
		let initObj = undefined;
		try {
			const mockInitScript = component.properties["mockInitScript"];
			const initScript = eval(mockInitScript);
			initObj = { ...(await eval("async () => {" + initScript + "}")()) };
		} catch (error) {
			initObj = { page: {}, actions: {}, params: {} };
			alert("Invalid Mock Init Script");
			console.log(error);
		}
		// TODO actions need to override the any predefined action
		const { page, actions, params } = initObj;
		const pageActions = eval(component.properties["pageActions"]) as CDashboardModulePageAction[];
		setPageDefinedActions(pageActions);
		if (initObj["config"]) setDashboardConfig(initObj["config"]);
		setDesignerPageContext(page as Context);
		setDesignerPageParams(params as PageParams);
		const content = eval(component.properties["pageContent"]);
		setPageContent(content);
		setSelectedComponent(content);
	}, [refreshDesign]);

	useEffect(() => {
		prepare();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [refreshDesign]);

	useEffect(() => {
		if (pageContent) {
			eval(component.properties["pageContent"] + "=pageContent");
			updateContext({ ...context });
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [modVersion]);

	const clearError = () => setHasError({ hasError: false });

	const updateComponentInDom = (component: CDashboardModuleTabComponent) => {
		if (pageContent) {
			processDomTree(pageContent, (toProcessComponent) => {
				if (toProcessComponent.id === component.id) {
					toProcessComponent.properties = { ...component.properties };
					toProcessComponent.type = component.type;
					toProcessComponent.styles = component.styles;
				}
			});
			setPageContent(JSON.parse(JSON.stringify(pageContent)));
			setSelectedComponent(JSON.parse(JSON.stringify(component)));
			setModVersion((prev) => prev + 1);
		}
	};

	const updateSelectedComponent = (component: CDashboardModuleTabComponent) => {
		if (component) setSelectedComponent(JSON.parse(JSON.stringify(component)));
	};

	const handleCloseInsertDomElementMenu = () => {
		setAnchorDomElementMenu(null);
	};

	const handleClickInsertDomElementMenu = (event: React.MouseEvent<HTMLButtonElement>) => {
		setAnchorDomElementMenu(event.currentTarget);
	};

	const insertChild = (type: string) => {
		handleCloseInsertDomElementMenu();
		if (pageContent) {
			processDomTree(pageContent, (toProcessComponent) => {
				if (toProcessComponent.id === selectedComponent?.id) {
					const properties = {} as Record<string, string>;
					componentsMap[type].schema.forEach((f) => {
						if (f.required && f.defaultValue) {
							properties[f.name] = f.defaultValue as string;
						}
					});
					const newComp = {
						id: uuidv4(),
						type: type,
						properties: properties,
						childern: [],
					} as CDashboardModuleTabComponent;
					toProcessComponent.childern.push(newComp);
				}
			});
			setPageContent(JSON.parse(JSON.stringify(pageContent)));
			setModVersion((prev) => prev + 1);
		}
	};

	const deleteSelected = () => {
		if (pageContent && selectedComponent) {
			const parent = findParentComponent(pageContent, selectedComponent.id);
			const modPageContent = deleteComponent(pageContent, selectedComponent.id);
			if (parent) setSelectedComponent(parent);
			setPageContent(JSON.parse(JSON.stringify(modPageContent)));
			setModVersion((prev) => prev + 1);
		}
	};

	const moveElementUp = () => {
		if (pageContent && selectedComponent) {
			const parentComp = findParentComponent(pageContent, selectedComponent.id);
			if (parentComp && parentComp.childern) {
				const index = parentComp.childern.findIndex((c) => c.id === selectedComponent.id);
				if (index === 0) return;
				const temp = parentComp.childern[index - 1];
				parentComp.childern[index - 1] = selectedComponent;
				parentComp.childern[index] = temp;
				setPageContent(JSON.parse(JSON.stringify(pageContent)));
				setModVersion((prev) => prev + 1);
			}
		}
	};

	const moveElementDown = () => {
		if (pageContent && selectedComponent) {
			const parentComp = findParentComponent(pageContent, selectedComponent.id);
			if (parentComp && parentComp.childern) {
				const index = parentComp.childern.findIndex((c) => c.id === selectedComponent.id);
				if (index === parentComp.childern.length - 1) return;
				const temp = parentComp.childern[index + 1];
				parentComp.childern[index + 1] = selectedComponent;
				parentComp.childern[index] = temp;
				setPageContent(JSON.parse(JSON.stringify(pageContent)));
				setModVersion((prev) => prev + 1);
			}
		}
	};

	const wrapSelected = () => {
		if (pageContent && selectedComponent) {
			const parentComp = findParentComponent(pageContent, selectedComponent.id);
			if (parentComp) {
				const index = parentComp.childern.findIndex((com: CDashboardModuleTabComponent) => com.id === selectedComponent.id);
				const curComp = parentComp.childern[index];
				parentComp.childern[index] = {
					id: uuidv4(),
					type: "Box",
					properties: {},
					childern: [curComp],
				} as CDashboardModuleTabComponent;
				setPageContent(JSON.parse(JSON.stringify(pageContent)));
				setModVersion((prev) => prev + 1);
			}
		}
	};

	const copySelected = () => {
		console.log(pageContent, selectedComponent);
		if (pageContent && selectedComponent) {
			const clone = JSON.parse(JSON.stringify(selectedComponent));
			clone.id = uuidv4();
			processDomTree(clone, (component) => {
				component.id = uuidv4();
			});
			const clipboardItems = [
				new ClipboardItem({
					"text/plain": new Blob([JSON.stringify({ builderObject: clone })], { type: "text/plain" }),
				}),
			];
			navigator.clipboard
				.write(clipboardItems)
				.then(() => {
					console.log("Data copied to clipboard successfully");
				})
				.catch((err) => {
					console.error("Unable to copy data to clipboard:", err);
				});
		}
	};

	const cutSelected = () => {
		if (pageContent && selectedComponent) {
			const clone = JSON.parse(JSON.stringify(selectedComponent));
			const clipboardItems = [
				new ClipboardItem({
					"text/plain": new Blob([JSON.stringify({ builderObject: clone })], { type: "text/plain" }),
				}),
			];
			navigator.clipboard
				.write(clipboardItems)
				.then(() => {
					console.log("Data copied to clipboard successfully");
				})
				.catch((err) => {
					console.error("Unable to copy data to clipboard:", err);
				});
			deleteSelected();
			setModVersion((prev) => prev + 1);
		}
	};

	const handlePaste = () => {
		navigator.clipboard
			.readText()
			.then((text) => {
				if (pageContent && selectedComponent) {
					try {
						const toPast = JSON.parse(text) as any;
						if (toPast["builderObject"]) {
							processDomTree(pageContent, (toProcessComponent) => {
								if (toProcessComponent.id === selectedComponent?.id) {
									toProcessComponent.childern.push(toPast["builderObject"]);
								}
							});
							setPageContent(JSON.parse(JSON.stringify(pageContent)));
							setModVersion((prev) => prev + 1);
						}
					} catch (_error) {}
				}
			})
			.catch((err) => {
				alert("Unable to read text from clipboard:" + err);
			});
	};

	return (
		<>
			{pageContent && (
				<>
					<Box sx={{ display: "flex", flex: 1, flexDirection: "column", gap: "10px", border: exeMode?.mode === Mode.DESIGN && component.id === exeMode?.activeComponent?.id ? "1px dashed red" : "initial" }}>
						<Box sx={{ display: "flex", flexDirection: "row", gap: "10px", flex: 1 }}>
							<Mosaic<string>
								renderTile={(id) => {
									return (
										<>
											{id === "domTreePanel" && (
												<Paper sx={{ flex: 1, display: "flex", flexDirection: "column", position: "absolute", top: 0, left: 0, right: 0, bottom: "50%" }}>
													<TabContext value={leftTopTabsValue}>
														<Box sx={{ borderBottom: 1, borderColor: "divider" }}>
															<TabList
																onChange={(event, value) => setLeftTopTabsValue(value)}
																sx={{
																	"& .MuiTabs-flexContainer": { alignItems: "stretch" },
																	"& .MuiTab-labelIcon": { minHeight: "50px", display: "flex", flexDirection: "row", justifyContent: "flex-start" },
																	"& .MuiButtonBase-root": { minWidth: "50px" },
																}}
															>
																<Tab label={"DOM Tree"} value="0" icon={<Icon>account_tree</Icon>} iconPosition="start" />
															</TabList>
														</Box>
														<TabPanel value="0" sx={{ padding: "0px", display: leftTopTabsValue === "0" ? "flex" : "none", flex: 1 }}>
															<Box sx={{ display: "flex", flexDirection: "column", position: "absolute", top: 50, left: 0, right: 0, bottom: 0 }}>
																<Stack direction="row" justifyContent="space-between" spacing={0}>
																	<div>
																		<IconButton
																			aria-controls={insertDomElementMenuOpen ? "insert-dom-element-menu" : undefined}
																			aria-haspopup="true"
																			aria-expanded={insertDomElementMenuOpen ? "true" : undefined}
																			id="insert-dom-element-button"
																			disabled={selectedComponent === undefined || componentsMap[selectedComponent.type].allowedChild?.length === 0}
																			onClick={handleClickInsertDomElementMenu}
																			color="secondary"
																		>
																			<Icon>add</Icon>
																		</IconButton>
																		<Menu
																			open={insertDomElementMenuOpen}
																			id="insert-dom-element-menu"
																			onClose={handleCloseInsertDomElementMenu}
																			anchorEl={anchorDomElementMenu}
																			MenuListProps={{
																				"aria-labelledby": "insert-dom-element-button",
																			}}
																		>
																			{selectedComponent &&
																				Object.keys(componentsMap)
																					.filter((c) => !componentsMap[selectedComponent.type].allowedChild || componentsMap[selectedComponent.type].allowedChild?.indexOf(c) != -1)
																					.sort()
																					.map((type) => (
																						<MenuItem key={type} onClick={() => insertChild(type)} disableRipple>
																							<ListItemIcon>
																								<Icon color="info">{componentsMap[type].icon ? componentsMap[type].icon : "add"}</Icon>
																							</ListItemIcon>
																							<ListItemText>
																								<Typography variant="button">{componentsMap[type].name}</Typography>
																							</ListItemText>
																						</MenuItem>
																					))}
																		</Menu>
																	</div>
																	<Tooltip title="Wrap">
																		<span>
																			<IconButton disabled={selectedComponent === undefined} onClick={wrapSelected} color="primary">
																				<Icon>create_new_folder</Icon>
																			</IconButton>
																		</span>
																	</Tooltip>
																	<Tooltip title="Move Up">
																		<span>
																			<IconButton disabled={selectedComponent === undefined || selectedComponent.id === pageContent.id} onClick={moveElementUp} color="primary">
																				<Icon>arrow_upward</Icon>
																			</IconButton>
																		</span>
																	</Tooltip>
																	<Tooltip title="Move Down">
																		<span>
																			<IconButton disabled={selectedComponent === undefined || selectedComponent.id === pageContent.id} onClick={moveElementDown} color="primary">
																				<Icon>arrow_downward</Icon>
																			</IconButton>
																		</span>
																	</Tooltip>
																	<Tooltip title="Copy">
																		<span>
																			<IconButton disabled={selectedComponent === undefined} onClick={copySelected} color="primary">
																				<Icon>content_copy</Icon>
																			</IconButton>
																		</span>
																	</Tooltip>
																	<Tooltip title="Cut">
																		<span>
																			<IconButton disabled={selectedComponent === undefined} onClick={cutSelected} color="primary">
																				<Icon>content_cut</Icon>
																			</IconButton>
																		</span>
																	</Tooltip>
																	<Tooltip title="Paste">
																		<span>
																			<IconButton onClick={handlePaste} color="primary">
																				<Icon>content_paste</Icon>
																			</IconButton>
																		</span>
																	</Tooltip>
																	<Tooltip title="Delete">
																		<span>
																			<IconButton disabled={selectedComponent === undefined || selectedComponent.id === pageContent.id} onClick={deleteSelected} color="warning">
																				<Icon>delete</Icon>
																			</IconButton>
																		</span>
																	</Tooltip>
																	<Tooltip title="Refresh">
																		<IconButton
																			onClick={() => {
																				clearError();
																				setRefreshDesign((prev) => prev + 1);
																			}}
																			color="primary"
																		>
																			<Icon>refresh</Icon>
																		</IconButton>
																	</Tooltip>
																	<Tooltip title="Locator">
																		<IconButton
																			onClick={() => {
																				setLocatorActivated((prev) => !prev);
																			}}
																			color={locatorActivated ? "error" : "success"}
																		>
																			<Icon>my_location</Icon>
																		</IconButton>
																	</Tooltip>
																</Stack>
																<Divider sx={{ marginBottom: 1 }} />
																{selectedComponent && (
																	<Box sx={{ overflow: "auto", position: "relative", top: 0, left: 0, right: 0, bottom: 0 }}>
																		<ComponentDomTree
																			component={pageContent}
																			selectedComponent={selectedComponent}
																			locateComponent={locateComponent}
																			selectionChange={(component) => {
																				updateSelectedComponent(component);
																			}}
																			setLocateComponent={setLocateComponent}
																		/>
																	</Box>
																)}
															</Box>
														</TabPanel>
													</TabContext>
												</Paper>
											)}
											{id === "domPropsPanel" && (
												<Paper sx={{ flex: 1, display: "flex", flexDirection: "column", position: "absolute", top: 0, left: 0, right: 0, bottom: 0 }}>
													<TabContext value={leftBottomTabsValue}>
														<Box sx={{ borderBottom: 1, borderColor: "divider" }}>
															<TabList
																onChange={(event, value) => setLeftBottomTabsValue(value)}
																sx={{
																	"& .MuiTabs-flexContainer": { alignItems: "stretch" },
																	"& .MuiTab-labelIcon": { minHeight: "50px", display: "flex", flexDirection: "row", justifyContent: "flex-start" },
																	"& .MuiButtonBase-root": { minWidth: "50px" },
																}}
															>
																<Tab label="DOM Properties" value="0" icon={<Icon>settings</Icon>} iconPosition="start" />
																<Tab label="Styles" value="1" icon={<Icon>css</Icon>} iconPosition="start" />
															</TabList>
														</Box>
														<TabPanel value="0" sx={{ padding: "0px", flex: 1, position: "relative" }}>
															{selectedComponent && (
																<Box sx={{ overflowY: "auto", padding: 1, flex: 1, position: "absolute", top: 0, left: 0, right: 0, bottom: 0 }}>
																	<ComponentProperties
																		key={selectedComponent.id}
																		component={selectedComponent}
																		pageActions={pageDefinedActions || []}
																		componentUpdated={(component) => {
																			updateComponentInDom(component);
																		}}
																	/>
																</Box>
															)}
														</TabPanel>
														<TabPanel value="1" sx={{ padding: "0px", flex: 1, position: "relative" }}>
															<Box sx={{ overflowY: "auto", padding: 1, flex: 1, position: "absolute", top: 0, left: 0, right: 0, bottom: 0 }}>
																{selectedComponent && (
																	<>
																		<Editor
																			key={selectedComponent.id}
																			defaultValue={selectedComponent.styles ? (typeof selectedComponent.styles === "object" ? objectToString(selectedComponent.styles) : selectedComponent.styles) : ""}
																			defaultLanguage="javascript"
																			options={{
																				minimap: { enabled: false },
																				lineNumbers: "off",
																				fontSize: 12,
																				wordWrap: "on",
																				glyphMargin: false,
																				folding: false,
																				lineDecorationsWidth: 0,
																				lineNumbersMinChars: 0,
																				formatOnPaste: true,
																			}}
																			onMount={(editor, _monaco) => {
																				editor.getAction("editor.action.formatDocument")?.run();
																				editor.onDidBlurEditorText(() => {
																					editor.getAction("editor.action.formatDocument")?.run();
																					selectedComponent.styles = editor.getValue();
																					updateComponentInDom(JSON.parse(JSON.stringify(selectedComponent)));
																				});
																			}}
																			onChange={(newValue: string | undefined) => {}}
																		/>
																	</>
																)}
															</Box>
														</TabPanel>
													</TabContext>
												</Paper>
											)}
											{id === "previewPanel" && (
												<Paper sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
													<TabContext value={centerTabsValue}>
														<Box sx={{ borderBottom: 1, borderColor: "divider" }}>
															<TabList
																onChange={(event, value) => setCenterTabsValue(value)}
																sx={{
																	"& .MuiTabs-flexContainer": { alignItems: "stretch" },
																	"& .MuiTab-labelIcon": { minHeight: "50px", display: "flex", flexDirection: "row", justifyContent: "flex-start" },
																	"& .MuiButtonBase-root": { minWidth: "50px" },
																}}
															>
																<Tab label="Page Preview" value="0" icon={<Icon>preview</Icon>} iconPosition="start" />
																<Tab label="Page Context" value="1" icon={<Icon>data_object</Icon>} iconPosition="start" />
															</TabList>
														</Box>
														<TabPanel value="0" sx={{ padding: "0px", flex: 1, display: centerTabsValue === "0" ? "flex" : "none" }}>
															<Box sx={{ display: "flex", overflow: "auto", flex: 1, padding: 1 }}>
																<DashboardEnvContext.Provider value={{ navigate: () => {}, config: dashboardConfig, utils: dashboard?.utils }}>
																	<DashboardPageContext.Provider
																		value={
																			{
																				context: designerPageContext,
																				params: designerPageParams,
																				updateContext: updateDesignerContext,
																				activatePageLoader: (status: boolean) => setPageLoaderActive(status),
																			} as PageContext
																		}
																	>
																		<ExecutionTypeContext.Provider
																			value={{
																				mode: Mode.DESIGN,
																				activeComponent: selectedComponent,
																				locatorActivated: locatorActivated,
																				logError: logConsoleError,
																				updateActiveComponent: (comp) => {
																					setSelectedComponent(comp);
																					setLocatorActivated(false);
																					setLocateComponent({ ...comp });
																				},
																			}}
																		>
																			<Box sx={{ flex: 1, display: "flex", position: "relative" }}>
																				{pageLoaderActive && (
																					<CircularProgressFullScreenWrapper>
																						<CircularProgressWrapper>
																							<CircularProgress />
																						</CircularProgressWrapper>
																					</CircularProgressFullScreenWrapper>
																				)}
																				<PageActionsBoundary actions={pageDefinedActions || []} key={refreshDesign}>
																					<ComponentDomPreview component={pageContent} />
																				</PageActionsBoundary>
																			</Box>
																		</ExecutionTypeContext.Provider>
																	</DashboardPageContext.Provider>
																</DashboardEnvContext.Provider>
															</Box>
														</TabPanel>
														<TabPanel value="1" sx={{ padding: "0px", flex: 1, display: centerTabsValue === "1" ? "flex" : "none", position: "relative" }}>
															<Box sx={{ display: "flex", overflow: "auto", flex: 1, padding: 1, position: "absolute", top: 0, left: 0, right: 0, bottom: 0 }}>
																<ReactJson src={designerPageContext || {}} />
															</Box>
														</TabPanel>
													</TabContext>
												</Paper>
											)}
											{id === "consolePanel" && (
												<Paper sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
													<TabContext value={consoleTabsValue}>
														<Box sx={{ borderBottom: 1, borderColor: "divider" }}>
															<TabList
																onChange={(event, value) => setConsoleTabsValue(value)}
																sx={{
																	"& .MuiTabs-flexContainer": { alignItems: "stretch" },
																	"& .MuiTab-labelIcon": { minHeight: "50px", display: "flex", flexDirection: "row", justifyContent: "flex-start" },
																	"& .MuiButtonBase-root": { minWidth: "50px" },
																}}
															>
																<Tab label="Console" value="0" icon={<Icon>terminal</Icon>} iconPosition="start" />
															</TabList>
														</Box>
														<TabPanel value="0" sx={{ padding: "0px", display: "flex", flex: 1, position: "relative" }}>
															<Box sx={{ overflow: "auto", flex: 1, position: "absolute", top: 0, right: 0, bottom: 0, left: 0 }}>
																<Button onClick={clearError} sx={{ position: "absolute", top: 0, right: 5 }}>
																	Clear
																</Button>
																{hasError.hasError === true && (
																	<>
																		<pre style={{ whiteSpace: "pre-wrap", margin: 0, marginTop: "5px", fontSize: "12px", color: "red", paddingLeft: "10px", paddingRight: "10px" }}>{hasError.error}</pre>
																	</>
																)}
															</Box>
														</TabPanel>
													</TabContext>
												</Paper>
											)}
										</>
									);
								}}
								initialValue={designerLayout}
							/>
						</Box>
					</Box>
				</>
			)}
		</>
	);
}

export default Component;
